
import React, { useState, useEffect } from 'react';
import { PlusCircle, Trash2, Package, Sparkles, AlertTriangle, Image as ImageIcon, Wand2, RefreshCw } from 'lucide-react';
import { collection, addDoc, onSnapshot, deleteDoc, doc, updateDoc } from "firebase/firestore";
import { GoogleGenAI } from "@google/genai";
import { db } from './firebaseConfig';
import { Product } from './types';
import { LOGO_PROMPT } from './constants';

export const AdminDashboard = () => {
    const [products, setProducts] = useState<Product[]>([]);
    const [name, setName] = useState('');
    const [price, setPrice] = useState('');
    const [category, setCategory] = useState<Product['category']>('clothing');
    const [imageUrl, setImageUrl] = useState('');
    const [isEligible, setIsEligible] = useState(true);
    const [loading, setLoading] = useState(false);

    // Brand Gen state
    const [isGenLoading, setIsGenLoading] = useState(false);
    const [generatedLogo, setGeneratedLogo] = useState<string | null>(null);

    useEffect(() => {
        // 🔑 CRITICAL: Block ALL snapshots until auth is ready
        // Note: "products" is a public collection, but we still wait for auth to be ready
        // Admin dashboard should wait for auth to verify admin role
        if (!authReady) return;
        
        const unsubscribe = onSnapshot(collection(db, "products"), (snapshot) => {
            setProducts(snapshot.docs.map(d => ({ id: d.id, ...d.data() } as Product)));
        }, (error) => {
            // Handle errors gracefully
            if (import.meta.env.DEV && error?.code !== 'permission-denied') {
                console.warn('[ADMIN] Error loading products:', error);
            }
            setProducts([]);
        });
        return () => unsubscribe();
    }, [authReady]);

    const generateBrandLogo = async () => {
        setIsGenLoading(true);
        try {
            const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
            const response = await ai.models.generateContent({
                model: 'gemini-2.5-flash-image',
                contents: {
                    parts: [{ text: LOGO_PROMPT }]
                },
                config: {
                    imageConfig: {
                        aspectRatio: "1:1"
                    }
                }
            });

            const part = response.candidates?.[0]?.content?.parts?.find(p => p.inlineData);
            if (part?.inlineData) {
                setGeneratedLogo(`data:image/png;base64,${part.inlineData.data}`);
            }
        } catch (error) {
            console.error("Logo Generation Error:", error);
            alert("Generation failed. Check API Key permissions.");
        } finally {
            setIsGenLoading(false);
        }
    };

    const handleAddProduct = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name || !price || !imageUrl) return;
        setLoading(true);
        try {
            await addDoc(collection(db, "products"), {
                name,
                price: parseFloat(price),
                category,
                image: imageUrl,
                stock: 999,
                rating: 5,
                supplierId: 'admin',
                isReferralEligible: isEligible,
                maxReferralDiscount: 100
            });
            setName(''); setPrice(''); setImageUrl('');
        } catch (error) { alert("Failed"); } finally { setLoading(false); }
    };

    const toggleEligibility = async (product: Product) => {
        await updateDoc(doc(db, "products", product.id), {
            isReferralEligible: !product.isReferralEligible
        });
    };

    return (
        <div className="p-4 md:p-8 pb-24 space-y-12">
            <h1 className="text-4xl font-black flex items-center gap-3 text-cyan-400 italic tracking-tighter">
                <Sparkles size={40} /> VIRAL OPS CENTER
            </h1>

            {/* Brand Identity Section */}
            <section className="bg-slate-900/50 border border-slate-800 rounded-[3rem] p-8 shadow-2xl space-y-8">
                <div className="flex justify-between items-center">
                    <div>
                        <h2 className="text-2xl font-black text-white flex items-center gap-2 uppercase tracking-tight">
                            <Wand2 className="text-cyan-400"/> Brand Identity Lab
                        </h2>
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-widest mt-1">AI-Powered Aesthetic Synthesis</p>
                    </div>
                    <button 
                        onClick={generateBrandLogo}
                        disabled={isGenLoading}
                        className="bg-white text-black px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest flex items-center gap-2 hover:bg-cyan-400 transition-all shadow-lg shadow-cyan-500/10 active:scale-95"
                    >
                        {isGenLoading ? <RefreshCw className="animate-spin" size={16}/> : <Sparkles size={16}/>}
                        {isGenLoading ? 'Generating...' : 'Regenerate 3D Logo'}
                    </button>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                    <div className="space-y-4">
                        <div className="bg-black/40 border border-slate-800 p-6 rounded-[2rem]">
                            <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-3">AI PROMPT CONFIG</h4>
                            <p className="text-xs font-mono text-slate-400 leading-relaxed italic">
                                {LOGO_PROMPT}
                            </p>
                        </div>
                    </div>
                    <div className="flex justify-center items-center">
                        <div className="relative group">
                            <div className="absolute inset-0 bg-cyan-500/20 blur-[50px] rounded-full group-hover:bg-cyan-500/40 transition-all" />
                            <div className="relative w-48 h-48 bg-slate-900 border-2 border-slate-800 rounded-[2.5rem] overflow-hidden flex items-center justify-center shadow-2xl">
                                {generatedLogo ? (
                                    <img src={generatedLogo} className="w-full h-full object-cover" />
                                ) : (
                                    <ImageIcon className="text-slate-800" size={64} />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <div className="grid lg:grid-cols-2 gap-8">
                <div className="bg-slate-900 p-8 rounded-[2.5rem] border border-slate-800 shadow-xl space-y-8">
                    <h2 className="text-xl font-bold flex items-center gap-2 uppercase italic tracking-tighter"><PlusCircle className="text-cyan-400"/> Inventory Intake</h2>
                    <form onSubmit={handleAddProduct} className="space-y-4">
                        <input className="w-full bg-slate-950 border border-slate-800 rounded-xl p-4 outline-none focus:border-cyan-500 font-bold transition-all" placeholder="Product Name" value={name} onChange={e => setName(e.target.value)} required />
                        <div className="grid grid-cols-2 gap-4">
                            <input type="number" step="0.01" className="w-full bg-slate-950 border border-slate-800 rounded-xl p-4 outline-none focus:border-cyan-500 font-bold" placeholder="Price €" value={price} onChange={e => setPrice(e.target.value)} required />
                            <select className="w-full bg-slate-950 border border-slate-800 rounded-xl p-4 outline-none font-bold" value={category} onChange={e => setCategory(e.target.value as any)}>
                                <option value="clothing">Clothing</option>
                                <option value="equipment">Equipment</option>
                                <option value="supplements">Supplements</option>
                            </select>
                        </div>
                        <input className="w-full bg-slate-950 border border-slate-800 rounded-xl p-4 outline-none focus:border-cyan-500 font-bold" placeholder="Image URL" value={imageUrl} onChange={e => setImageUrl(e.target.value)} required />
                        
                        <div className="flex items-center gap-3 p-4 bg-cyan-500/5 border border-cyan-500/20 rounded-xl">
                            <input type="checkbox" id="eligible" checked={isEligible} onChange={e => setIsEligible(e.target.checked)} className="w-5 h-5 accent-cyan-500" />
                            <label htmlFor="eligible" className="text-sm font-bold text-cyan-100">Eligible for Referral Scounts</label>
                        </div>

                        <button type="submit" disabled={loading} className="w-full bg-cyan-500 hover:bg-cyan-600 text-black py-4 rounded-2xl font-black shadow-lg shadow-cyan-500/20 transition-all active:scale-95">{loading ? '...' : 'Publish to Shop'}</button>
                    </form>
                </div>

                <div className="bg-slate-900 p-8 rounded-[2.5rem] border border-slate-800 shadow-xl overflow-hidden flex flex-col">
                    <h2 className="text-xl font-bold mb-6 flex items-center justify-between uppercase italic tracking-tighter">
                        <span className="flex items-center gap-2"><Package className="text-cyan-400"/> Catalog</span>
                        <span className="text-xs bg-slate-800 px-3 py-1 rounded-full text-slate-400 font-mono">{products.length} Items</span>
                    </h2>
                    <div className="space-y-3 overflow-y-auto flex-1 pr-2">
                        {products.map(p => (
                            <div key={p.id} className="bg-slate-950/50 p-4 rounded-2xl border border-slate-800 flex items-center gap-4 group hover:border-cyan-500/30 transition-all">
                                <img src={p.image} className="w-14 h-14 rounded-xl object-cover shadow-lg" />
                                <div className="flex-1 min-w-0">
                                    <p className="font-bold text-sm truncate text-white">{p.name}</p>
                                    <div className="flex items-center gap-2 mt-1">
                                        <span className="text-cyan-400 text-xs font-mono">€{p.price.toFixed(2)}</span>
                                        <button onClick={() => toggleEligibility(p)} className={`text-[9px] px-2 py-0.5 rounded-full font-black uppercase tracking-tighter transition-all ${p.isReferralEligible ? 'bg-cyan-500/20 text-cyan-500' : 'bg-slate-800 text-slate-600'}`}>
                                            {p.isReferralEligible ? 'Eligible' : 'Fixed'}
                                        </button>
                                    </div>
                                </div>
                                <button onClick={() => deleteDoc(doc(db, "products", p.id))} className="p-2 text-slate-600 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"><Trash2 size={18}/></button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
